import { useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { StatusChip } from "../components/ui/StatusChip";
import { OwnerTag } from "../components/ui/OwnerTag";
import { PriorityDot } from "../components/ui/PriorityDot";
import { EmptyState } from "../components/ui/EmptyState";
import type { Card, CardStatus, Owner } from "../data/types";
import { getTaskMeta, isOverdue, sortCardsByExecution } from "../lib/taskMeta";

interface PathPageProps {
  cards: Card[];
  onSelectCard: (cardId: string) => void;
}

interface DayGroup {
  date: string;
  label: string;
  cards: Card[];
}

function formatDateLabel(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
  });
}

function normalizeDateKey(date: string): string {
  return date.slice(0, 10);
}

export function PathPage({ cards, onSelectCard }: PathPageProps) {
  const [ownerFilter, setOwnerFilter] = useState<Owner | "all">("all");
  const [statusFilter, setStatusFilter] = useState<CardStatus | "all">("all");

  const todayIso = new Date().toISOString().slice(0, 10);

  const cardStatusMap = useMemo(() => {
    return new Map(cards.map((card) => [card.id, card.status]));
  }, [cards]);

  const runbookCards = useMemo(() => {
    const filtered = cards.filter((card) => {
      const meta = getTaskMeta(card);
      const isOneOff = meta.taskType === "one_off" || (meta.taskType === "unknown" && Boolean(card.targetDate));
      if (!isOneOff) return false;
      if (ownerFilter !== "all" && card.owner !== ownerFilter) return false;
      if (statusFilter !== "all" && card.status !== statusFilter) return false;
      return true;
    });

    return sortCardsByExecution(filtered);
  }, [cards, ownerFilter, statusFilter]);

  const dayGroups = useMemo(() => {
    const grouped = new Map<string, Card[]>();

    for (const card of runbookCards) {
      const dayKey = normalizeDateKey(card.targetDate ?? todayIso);
      const bucket = grouped.get(dayKey) ?? [];
      bucket.push(card);
      grouped.set(dayKey, bucket);
    }

    const result: DayGroup[] = [];
    for (const [date, groupCards] of grouped.entries()) {
      result.push({
        date,
        label: formatDateLabel(date),
        cards: groupCards,
      });
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }, [runbookCards, todayIso]);

  const totalCards = runbookCards.length;
  const doneCards = runbookCards.filter((card) => card.status === "done").length;
  const overdueCards = runbookCards.filter((card) => isOverdue(card, todayIso)).length;

  return (
    <div className="h-full flex flex-col">
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-3)]">
        <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Runbook Diario</span>
        <span className="text-[14px] tabular-nums text-[var(--color-text-muted)]">{doneCards}/{totalCards} done</span>
        <span className={cn(
          "text-[11px] rounded-[var(--radius-sm)] px-[6px] py-[2px] border",
          overdueCards > 0
            ? "text-[var(--color-state-error)] border-[var(--color-state-error-border)] bg-[var(--color-state-error-bg)]"
            : "text-[var(--color-text-muted)] border-[var(--color-border-stroke)] bg-[var(--color-bg-canvas)]",
        )}>
          {overdueCards > 0 ? `${overdueCards} overdue` : "SLA healthy"}
        </span>

        <div className="ml-auto flex items-center gap-[var(--spacing-2)]">
          <select
            value={ownerFilter}
            onChange={(event) => setOwnerFilter(event.target.value as Owner | "all")}
            className={cn(
              "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
              "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
              "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
              "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
            )}
          >
            <option value="all">All Owners</option>
            <option value="Joel">Joel</option>
            <option value="Albert">Albert</option>
            <option value="AI Wave">AI Wave</option>
            <option value="Joint">Joint</option>
          </select>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as CardStatus | "all")}
            className={cn(
              "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
              "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
              "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
              "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
            )}
          >
            <option value="all">All Statuses</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="blocked">Blocked</option>
            <option value="done">Done</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="w-full px-[var(--spacing-4)] py-[var(--spacing-6)]">
          {dayGroups.length === 0 ? (
            <EmptyState title="No one-off tasks" description="Adjust filters or import the GTM v2 CSV." variant="search" />
          ) : (
            <div className="space-y-[var(--spacing-6)]">
              {dayGroups.map((group) => (
                <section key={group.date}>
                  <div className="flex items-center gap-[var(--spacing-2)] mb-[var(--spacing-2)]">
                    <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">{group.label}</span>
                    <span className="text-[14px] tabular-nums text-[var(--color-text-muted)]">{group.cards.filter((card) => card.status === "done").length}/{group.cards.length}</span>
                  </div>
                  <div className="space-y-[var(--spacing-1)]">
                    {group.cards.map((card) => {
                      const meta = getTaskMeta(card);
                      const pendingDependencies = card.dependsOn.filter((dependencyId) => cardStatusMap.get(dependencyId) !== "done").length;
                      const overdue = isOverdue(card, todayIso);

                      return (
                        <button
                          key={card.id}
                          onClick={() => onSelectCard(card.id)}
                          className={cn(
                            "w-full text-left flex items-center gap-[var(--spacing-3)] rounded-[var(--radius-md)]",
                            "px-[var(--spacing-3)] py-[var(--spacing-2)]",
                            "border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
                            "hover:bg-[var(--color-bg-hover)] hover:border-[var(--color-bg-hover)]",
                            "transition-all duration-[var(--duration-fast)] cursor-pointer",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-canvas)]",
                            card.status === "done" && "opacity-55",
                          )}
                        >
                          <PriorityDot priority={card.priority} />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-[var(--spacing-2)]">
                              <span
                                className={cn(
                                  "text-[14px] leading-[20px] text-[var(--color-text-primary)] truncate",
                                  card.status === "done" && "line-through",
                                )}
                              >
                                {card.title}
                              </span>

                              {meta.executionWindow && (
                                <span className="text-[11px] rounded-[var(--radius-sm)] px-[6px] py-[2px] border border-[var(--color-border-stroke)] text-[var(--color-text-muted)] whitespace-nowrap">
                                  {meta.executionWindow.replace("_CET", " CET")}
                                </span>
                              )}

                              {meta.sla && (
                                <span className="text-[11px] rounded-[var(--radius-sm)] px-[6px] py-[2px] border border-[var(--color-border-stroke)] text-[var(--color-text-muted)] whitespace-nowrap">
                                  SLA {meta.sla}
                                </span>
                              )}

                              {pendingDependencies > 0 && (
                                <span className="text-[11px] rounded-[var(--radius-sm)] px-[6px] py-[2px] border border-[var(--color-state-warning-border)] bg-[var(--color-state-warning-bg)] text-[var(--color-state-warning)] whitespace-nowrap">
                                  {pendingDependencies} blocked
                                </span>
                              )}

                              {overdue && (
                                <span className="text-[11px] rounded-[var(--radius-sm)] px-[6px] py-[2px] border border-[var(--color-state-error-border)] bg-[var(--color-state-error-bg)] text-[var(--color-state-error)] whitespace-nowrap">
                                  Overdue
                                </span>
                              )}
                            </div>
                          </div>

                          <OwnerTag owner={card.owner} />
                          <StatusChip status={card.status} />
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
